/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABTestingPanel - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ABTestingPanel.tsx
 * @version 1.0.0
 * @description Panel de A/B Testing para comparar endpoints de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - Crear nuevo test A/B
 * - Seleccionar dos endpoints/prompts a comparar
 * - Ver tests activos
 * - Comparar resultados lado a lado
 * - Métricas comparativas (interacciones, éxito, tiempo, rating)
 * - Declarar ganador
 * - Pausar/reanudar tests
 * - Ver historial de tests completados
 * 
 * NOTA: Este componente usa mock data porque el backend aún no tiene
 * endpoints de A/B testing implementados.
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  ProgressBar,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faPlus,
  faTrophy,
  faPause,
  faPlay,
  faStop,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faCoins,
  faStar,
  faInfoCircle,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import { ABTest } from '../../../../../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// MOCK DATA (temporal hasta que backend tenga endpoints)
// ═══════════════════════════════════════════════════════════════════════════

const MOCK_AB_TESTS: ABTest[] = [
  {
    id: 'test_001',
    name: 'GPT-4 vs Claude 3',
    description: 'Comparación de calidad de respuestas en español',
    variant_a: {
      endpoint_id: 'ep_001',
      name: 'IRC GPT-4'
    },
    variant_b: {
      endpoint_id: 'ep_002',
      name: 'Anubis Claude'
    },
    status: 'active',
    results: {
      variant_a: {
        interactions: 145,
        success_rate: 96.5,
        avg_response_time: 1450,
        avg_rating: 4.5
      },
      variant_b: {
        interactions: 138,
        success_rate: 94.2,
        avg_response_time: 1890,
        avg_rating: 4.3
      }
    },
    created_at: new Date(Date.now() - 86400000 * 3).toISOString()
  },
  {
    id: 'test_002',
    name: 'OpenRouter vs Local LLM',
    description: 'Test de velocidad y precisión',
    variant_a: {
      endpoint_id: 'ep_003',
      name: 'OpenRouter'
    },
    variant_b: {
      endpoint_id: 'ep_004',
      name: 'Local LLM'
    },
    status: 'completed',
    results: {
      variant_a: {
        interactions: 256,
        success_rate: 92.8,
        avg_response_time: 2100,
        avg_rating: 4.1
      },
      variant_b: {
        interactions: 248,
        success_rate: 89.5,
        avg_response_time: 950,
        avg_rating: 3.8
      }
    },
    winner: 'a',
    winner_reason: 'Mayor tasa de éxito y mejor rating promedio',
    created_at: new Date(Date.now() - 86400000 * 7).toISOString()
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const ABTestingPanel: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const { endpoints } = useAIEndpoints();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [tests, setTests] = useState<ABTest[]>(MOCK_AB_TESTS);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<ABTest | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    endpoint_a: '',
    endpoint_b: ''
  });

  // Filtrar solo endpoints activos con capacidad 'text'
  const testableEndpoints = endpoints.filter(
    ep => ep.enabled && ep.capabilities.includes('text')
  );

  // Separar tests por estado
  const activeTests = tests.filter(t => t.status === 'active');
  const completedTests = tests.filter(t => t.status === 'completed');

  // ───────────────────────────────────────────────────────────────────────
  // Handlers - Modals
  // ───────────────────────────────────────────────────────────────────────
  
  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      endpoint_a: '',
      endpoint_b: ''
    });
    setShowCreateModal(true);
  };

  const handleOpenResultsModal = (test: ABTest) => {
    setSelectedTest(test);
    setShowResultsModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowResultsModal(false);
    setSelectedTest(null);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Handlers - CRUD
  // ───────────────────────────────────────────────────────────────────────
  
  const handleCreateTest = () => {
    const endpointA = endpoints.find(ep => ep.id === formData.endpoint_a);
    const endpointB = endpoints.find(ep => ep.id === formData.endpoint_b);

    if (!endpointA || !endpointB) return;

    const newTest: ABTest = {
      id: `test_${Date.now()}`,
      name: formData.name,
      description: formData.description,
      variant_a: {
        endpoint_id: endpointA.id,
        name: endpointA.name
      },
      variant_b: {
        endpoint_id: endpointB.id,
        name: endpointB.name
      },
      status: 'active',
      results: {
        variant_a: {
          interactions: 0,
          success_rate: 0,
          avg_response_time: 0,
          avg_rating: 0
        },
        variant_b: {
          interactions: 0,
          success_rate: 0,
          avg_response_time: 0,
          avg_rating: 0
        }
      },
      created_at: new Date().toISOString()
    };

    setTests(prev => [newTest, ...prev]);
    handleCloseModals();
  };

  const handlePauseTest = (testId: string) => {
    setTests(prev =>
      prev.map(t =>
        t.id === testId ? { ...t, status: 'paused' as const } : t
      )
    );
  };

  const handleResumeTest = (testId: string) => {
    setTests(prev =>
      prev.map(t =>
        t.id === testId ? { ...t, status: 'active' as const } : t
      )
    );
  };

  const handleStopTest = (testId: string, winner: 'a' | 'b' | 'tie') => {
    setTests(prev =>
      prev.map(t =>
        t.id === testId
          ? {
              ...t,
              status: 'completed' as const,
              winner,
              winner_reason: winner === 'tie' 
                ? 'Resultados similares' 
                : `Variante ${winner.toUpperCase()} tuvo mejor performance`
            }
          : t
      )
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Create Modal
  // ───────────────────────────────────────────────────────────────────────
  
  const renderCreateModal = () => (
    <Modal show={showCreateModal} onHide={handleCloseModals} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFlask} className="me-2" />
          Crear Test A/B
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Nombre del Test *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: GPT-4 vs Claude 3"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe qué estás probando..."
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Variante A *</Form.Label>
                <Form.Select
                  value={formData.endpoint_a}
                  onChange={(e) => setFormData(prev => ({ ...prev, endpoint_a: e.target.value }))}
                >
                  <option value="">Selecciona endpoint...</option>
                  {testableEndpoints.map(ep => (
                    <option key={ep.id} value={ep.id} disabled={ep.id === formData.endpoint_b}>
                      {ep.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Variante B *</Form.Label>
                <Form.Select
                  value={formData.endpoint_b}
                  onChange={(e) => setFormData(prev => ({ ...prev, endpoint_b: e.target.value }))}
                >
                  <option value="">Selecciona endpoint...</option>
                  {testableEndpoints.map(ep => (
                    <option key={ep.id} value={ep.id} disabled={ep.id === formData.endpoint_a}>
                      {ep.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Alert variant="info">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            El test distribuirá automáticamente las interacciones entre ambas variantes.
          </Alert>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseModals}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateTest}
          disabled={!formData.name || !formData.endpoint_a || !formData.endpoint_b}
        >
          Crear Test
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // ───────────────────────────────────────────────────────────────────────
  // Render - Results Modal
  // ───────────────────────────────────────────────────────────────────────
  
  const renderResultsModal = () => {
    if (!selectedTest) return null;

    const { variant_a, variant_b } = selectedTest.results;

    return (
      <Modal show={showResultsModal} onHide={handleCloseModals} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faChartBar} className="me-2" />
            Resultados: {selectedTest.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Header con status */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <Badge bg={
                selectedTest.status === 'active' ? 'success' :
                selectedTest.status === 'paused' ? 'warning' : 'secondary'
              } className="me-2">
                {selectedTest.status}
              </Badge>
              {selectedTest.winner && (
                <Badge bg="primary">
                  <FontAwesomeIcon icon={faTrophy} className="me-1" />
                  Ganador: Variante {selectedTest.winner.toUpperCase()}
                </Badge>
              )}
            </div>
            <small className="text-muted">
              Creado: {new Date(selectedTest.created_at).toLocaleDateString()}
            </small>
          </div>

          {/* Comparación lado a lado */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="border-primary h-100">
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">
                    Variante A: {selectedTest.variant_a.name}
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted d-block">Interacciones</small>
                    <h4>{variant_a.interactions}</h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Tasa de Éxito</small>
                    <h4>{variant_a.success_rate}%</h4>
                    <ProgressBar
                      now={variant_a.success_rate}
                      variant="success"
                      style={{ height: 8 }}
                    />
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Tiempo Promedio</small>
                    <h4>{variant_a.avg_response_time}ms</h4>
                  </div>
                  <div>
                    <small className="text-muted d-block">Rating Promedio</small>
                    <h4>
                      <FontAwesomeIcon icon={faStar} className="text-warning me-2" />
                      {variant_a.avg_rating.toFixed(1)}
                    </h4>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="border-info h-100">
                <Card.Header className="bg-info text-white">
                  <h6 className="mb-0">
                    Variante B: {selectedTest.variant_b.name}
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted d-block">Interacciones</small>
                    <h4>{variant_b.interactions}</h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Tasa de Éxito</small>
                    <h4>{variant_b.success_rate}%</h4>
                    <ProgressBar
                      now={variant_b.success_rate}
                      variant="success"
                      style={{ height: 8 }}
                    />
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Tiempo Promedio</small>
                    <h4>{variant_b.avg_response_time}ms</h4>
                  </div>
                  <div>
                    <small className="text-muted d-block">Rating Promedio</small>
                    <h4>
                      <FontAwesomeIcon icon={faStar} className="text-warning me-2" />
                      {variant_b.avg_rating.toFixed(1)}
                    </h4>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Winner reason */}
          {selectedTest.winner_reason && (
            <Alert variant="success">
              <FontAwesomeIcon icon={faTrophy} className="me-2" />
              <strong>Conclusión:</strong> {selectedTest.winner_reason}
            </Alert>
          )}

          {/* Acciones */}
          {selectedTest.status === 'active' && (
            <div className="d-flex gap-2">
              <Button
                variant="warning"
                onClick={() => handlePauseTest(selectedTest.id)}
              >
                <FontAwesomeIcon icon={faPause} className="me-2" />
                Pausar Test
              </Button>
              <Button
                variant="success"
                onClick={() => handleStopTest(selectedTest.id, 'a')}
              >
                <FontAwesomeIcon icon={faTrophy} className="me-2" />
                Declarar Ganador: A
              </Button>
              <Button
                variant="info"
                onClick={() => handleStopTest(selectedTest.id, 'b')}
              >
                <FontAwesomeIcon icon={faTrophy} className="me-2" />
                Declarar Ganador: B
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleStopTest(selectedTest.id, 'tie')}
              >
                Declarar Empate
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModals}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Active Tests
  // ───────────────────────────────────────────────────────────────────────
  
  const renderActiveTests = () => {
    if (activeTests.length === 0) {
      return (
        <Alert variant="info">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          No hay tests activos. Crea uno nuevo para comenzar.
        </Alert>
      );
    }

    return (
      <Row>
        {activeTests.map(test => (
          <Col md={6} key={test.id} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="mb-1">{test.name}</h6>
                    <small className="text-muted">{test.description}</small>
                  </div>
                  <Badge bg="success">Activo</Badge>
                </div>

                <Row className="mb-3">
                  <Col>
                    <div className="text-center p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Variante A</small>
                      <strong>{test.variant_a.name}</strong>
                      <div className="mt-2">
                        <Badge bg="primary">{test.results.variant_a.interactions}</Badge>
                        <small className="text-muted ms-2">interacciones</small>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="text-center p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Variante B</small>
                      <strong>{test.variant_b.name}</strong>
                      <div className="mt-2">
                        <Badge bg="info">{test.results.variant_b.interactions}</Badge>
                        <small className="text-muted ms-2">interacciones</small>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleOpenResultsModal(test)}
                    className="flex-grow-1"
                  >
                    Ver Resultados
                  </Button>
                  {test.status === 'active' ? (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => handlePauseTest(test.id)}
                    >
                      <FontAwesomeIcon icon={faPause} />
                    </Button>
                  ) : (
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleResumeTest(test.id)}
                    >
                      <FontAwesomeIcon icon={faPlay} />
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Completed Tests
  // ───────────────────────────────────────────────────────────────────────
  
  const renderCompletedTests = () => {
    if (completedTests.length === 0) {
      return null;
    }

    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h6 className="mb-3">Tests Completados</h6>
          <Table hover>
            <thead className="bg-light">
              <tr>
                <th>Nombre</th>
                <th>Variantes</th>
                <th>Ganador</th>
                <th>Fecha</th>
                <th className="text-end">Acción</th>
              </tr>
            </thead>
            <tbody>
              {completedTests.map(test => (
                <tr key={test.id}>
                  <td>
                    <strong>{test.name}</strong>
                    <br />
                    <small className="text-muted">{test.description}</small>
                  </td>
                  <td>
                    <Badge bg="primary" className="me-1">{test.variant_a.name}</Badge>
                    vs
                    <Badge bg="info" className="ms-1">{test.variant_b.name}</Badge>
                  </td>
                  <td>
                    {test.winner ? (
                      <Badge bg="warning">
                        <FontAwesomeIcon icon={faTrophy} className="me-1" />
                        {test.winner === 'tie' ? 'Empate' : `Variante ${test.winner.toUpperCase()}`}
                      </Badge>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </td>
                  <td>
                    <small className="text-muted">
                      {new Date(test.created_at).toLocaleDateString()}
                    </small>
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleOpenResultsModal(test)}
                    >
                      Ver Detalles
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  if (testableEndpoints.length < 2) {
    return (
      <Alert variant="warning">
        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
        Necesitas al menos 2 endpoints activos con capacidad 'text' para crear tests A/B.
      </Alert>
    );
  }

  return (
    <div>
      {/* Warning - Mock Data */}
      <Alert variant="warning" className="mb-3">
        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
        <strong>Modo Demo:</strong> Este componente muestra datos de ejemplo. 
        Los endpoints de A/B testing aún no están implementados en el backend.
      </Alert>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faFlask} className="me-2 text-danger" />
          A/B Testing de Endpoints
          <Badge bg="secondary" className="ms-2">
            {activeTests.length} activos
          </Badge>
        </h5>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Nuevo Test A/B
        </Button>
      </div>

      {/* Active Tests */}
      <h6 className="mb-3">Tests Activos</h6>
      {renderActiveTests()}

      {/* Completed Tests */}
      {completedTests.length > 0 && (
        <div className="mt-4">
          {renderCompletedTests()}
        </div>
      )}

      {/* Modals */}
      {renderCreateModal()}
      {renderResultsModal()}
    </div>
  );
};

export default ABTestingPanel;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════